import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  signIn,
  signUp,
  signOut,
  updateProfile,
  submitVerification,
  silentRefresh,
  getProfile,
} from '../api/auth';
import { tokenManager } from '../../../shared/api/tokenManager';
import { logError } from '../../../shared/utils/errorUtils';
import type { User, UserFormData, VerificationFormData } from '../types/user';
import { SplashScreen } from '../../../shared/components/ui/SplashScreen';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (data: UserFormData, queryParams?: string) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<User>;
  submitVerification: (data: VerificationFormData) => Promise<User>;
  silentRefresh: () => Promise<User | null>;
  redirectToLogin: (message?: string, returnUrl?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [isSilentRefreshing, setIsSilentRefreshing] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize auth only once on mount
  useEffect(() => {
    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initAuth = async () => {
    const startTime = Date.now();
    const minimumLoadTime = 2000; // 2 seconds minimum

    try {
      // Check if we already have a valid token in memory
      const currentToken = tokenManager.accessToken;
      const currentUser = tokenManager.user;
      
      if (currentToken && currentUser) {
        // We have both token and user data, check if token is still valid
        try {
          // Try to fetch user profile to validate the token
          const userData = await getProfile();
          console.log('✅ Existing token is valid, user data confirmed');
          setUser(userData);
        } catch (error) {
          console.log('❌ Existing token is invalid, attempting silent refresh...');
          // Token is invalid, try silent refresh only if refresh token exists
          const refreshedUser = await handleSilentRefresh();
          if (refreshedUser) {
            setUser(refreshedUser);
          } else {
            // If refresh fails (no refresh token or other error), ensure no stale tokens are left
            console.log('🔄 Silent refresh failed - no refresh token or authentication error');
            tokenManager.clearTokens();
            setUser(null);
          }
        }
      } else {
        // No token or user data, attempt silent refresh only if refresh token exists
        console.log('🔄 No existing token, attempting silent refresh...');
        const refreshedUser = await handleSilentRefresh();
        if (refreshedUser) {
          setUser(refreshedUser);
        } else {
          // If refresh fails (no refresh token or other error), ensure no stale tokens are left
          console.log('🔄 Silent refresh failed - no refresh token or authentication error');
          tokenManager.clearTokens();
          setUser(null);
        }
      }
    } catch (error) {
      // Only log unexpected errors during auth initialization
      // Silent refresh failures (400/401) are expected and shouldn't be logged as errors
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status !== 400 && axiosError?.response?.status !== 401) {
      console.error('Error initializing auth:', error);
      }
      tokenManager.clearTokens();
      setUser(null);
    } finally {
      // Ensure minimum loading time of 2 seconds for consistent UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadTime - elapsedTime);
      
      if (remainingTime > 0) {
        console.log(`⏱️ Auth initialization completed in ${elapsedTime}ms, waiting ${remainingTime}ms for minimum load time`);
        setTimeout(() => {
          setLoading(false);
          setIsAuthInitialized(true);
        }, remainingTime);
      } else {
        console.log(`⏱️ Auth initialization completed in ${elapsedTime}ms (above minimum load time)`);
        setLoading(false);
        setIsAuthInitialized(true);
      }
    }
  };

  const handleSilentRefresh = useCallback(async (): Promise<User | null> => {
    // Don't show loading state for silent refresh
    if (isSilentRefreshing) {
      console.log('🔄 Silent refresh already in progress, skipping...');
      return null;
    }

    // Check if refresh token exists (it's stored as HttpOnly cookie)
    // We can't directly check the cookie, but we can try the refresh request
    // and handle the case where no refresh token is available
    console.log('🔄 Checking for refresh token availability...');
    
    setIsSilentRefreshing(true);
    
    try {
      console.log('🔄 Starting silent token refresh...');

      // Step 1: Get new access token (refresh endpoint only returns token + expiry)
      const response = await silentRefresh();
      if (!response || !response.access_token) {
        console.warn('❌ Silent refresh failed, no access token returned');
        return null;
      }

      console.log('✅ Token refresh successful, fetching user data...');

      // Step 2: Update token in tokenManager (preserve existing user data)
      tokenManager.updateToken(response.access_token);

      // Step 3: Fetch fresh user data using the new token
      try {
        const userData = await getProfile();
        console.log('✅ User data fetched successfully after token refresh');

        // Check if userData exists before accessing its properties
        if (userData && userData.id) {
          // Update tokenManager with fresh user data (backend handles auto-refresh)
          tokenManager.setTokens(
            response.access_token,
            userData,
            3600 // Default expiration, backend will auto-refresh when needed
          );
          return userData;
        } else {
          console.warn('⚠️ User data is undefined after token refresh');
          // Return existing user data if available
          const existingUser = tokenManager.user;
          if (existingUser) {
            console.log('🔄 Using existing user data after undefined user fetch');
            return existingUser;
          }
          return null;
        }
      } catch (userError) {
        console.error('⚠️ Failed to fetch user data after token refresh:', userError);
        // Even if user fetch fails, we still have a valid token
        // Return the existing user data if available
        const existingUser = tokenManager.user;
        if (existingUser) {
          console.log('🔄 Using existing user data after failed user fetch');
        }
        return existingUser;
      }
    } catch (error) {
      // Only log unexpected errors during silent refresh
      // 400/401 errors are expected when no refresh token is available
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status !== 400 && axiosError?.response?.status !== 401) {
      console.error('❌ Error refreshing token:', error);
      }
      tokenManager.clearTokens();
      return null;
    } finally {
      setIsSilentRefreshing(false);
    }
  }, [isSilentRefreshing]);

  const login = useCallback(async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ) => {
    try {
      // Clear any existing tokens before attempting login
      tokenManager.clearTokens();
      const response = await signIn(email, password, rememberMe);

      // Debug logging to see the actual response structure
      console.log('Login - Full response:', response);
      console.log('Login - Response keys:', Object.keys(response));
      console.log('Login - refresh_expires_in:', response.refresh_expires_in);

      // Check if user object exists before accessing its properties
      if (response.user && response.user.id) {
        console.log('Login - User type field:', response.user.type);
        console.log(
          'Login - User operator_type field:',
          response.user.operator_type
        );

        // Set tokens with default expiration (backend handles auto-refresh)
        tokenManager.setTokens(
          response.access_token,
          response.user,
          3600 // Default expiration, backend will auto-refresh when needed
        );
        setUser(response.user);
      } else {
        console.error('❌ Login response contains no user data');
        throw new Error('Login failed: No user data received');
      }

      // Handle redirect after successful login
      navigate('/dashboard', { replace: true });
    } catch (error) {
      tokenManager.clearTokens();
      setUser(null);
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      tokenManager.clearTokens();
      setUser(null);
      // Redirect to home and refresh to clear in-memory access token
      navigate('/', { replace: true });
      window.location.reload();
    }
  }, [navigate]);

  const signUpUser = useCallback(async (data: UserFormData, queryParams?: string) => {
    try {
      await signUp(data, queryParams);
    } catch (error) {
      logError(error, 'signUp');
      throw error;
    }
  }, []);

  const updateUserProfile = useCallback(async (data: Partial<User>) => {
    try {
      const updated = await updateProfile(data);
      setUser(prev => (prev ? { ...prev, ...updated } : updated));
      return updated;
    } catch (error) {
      logError(error, 'updateProfile');
      throw error;
    }
  }, []);

  const verifyUser = useCallback(async (data: VerificationFormData) => {
    try {
      const updated = await submitVerification(data);
      setUser(updated);
      return updated;
    } catch (error) {
      logError(error, 'submitVerification');
      throw error;
    }
  }, []);

  const redirectToLogin = useCallback((message?: string, returnUrl?: string) => {
    if (loading || isSilentRefreshing || (user && tokenManager.accessToken)) {
      return;
    }
    
    // Use URL parameters instead of React Router state for better compatibility
    const params = new URLSearchParams();
    if (message) {
      params.set('message', message);
    }
    if (returnUrl || location.pathname) {
      params.set('from', returnUrl || location.pathname);
    }
    
    const loginUrl = params.toString() ? `/login?${params.toString()}` : '/login';
    navigate(loginUrl);
  }, [loading, isSilentRefreshing, user, location.pathname, navigate]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user && tokenManager.accessToken && !loading),
    loading: loading && !isSilentRefreshing, // Don't show loading during silent refresh
    login,
    logout,
    signUp: signUpUser,
    updateProfile: updateUserProfile,
    submitVerification: verifyUser,
    silentRefresh: handleSilentRefresh,
    redirectToLogin,
  }), [
    user,
    loading,
    isSilentRefreshing,
    login,
    logout,
    signUpUser,
    updateUserProfile,
    verifyUser,
    handleSilentRefresh,
    redirectToLogin,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {isAuthInitialized ? children : <AuthLoadingScreen />}
    </AuthContext.Provider>
  );
};

export const AuthLoadingScreen: React.FC = () => {
  return <SplashScreen />;
};

export type { AuthContextType };
export { AuthContext };
